/**
 * ExpectedRowActions — Ted #32c / epic E3.1–E3.2 (#1158): the reservation's
 * own actions on the "Expected today" row.
 *
 * Ted (2026-10-06), on Gingr's Expected Today list: the checkmark checks the
 * dog in, the X cancels the reservation, the other options edit it. His main
 * point: "these reservation actions are reachable directly from Expected
 * Today". Until now the Front Desk and Dashboard rows could check in and
 * undo, and nothing else; Cancel and Edit lived only on the Bookings page.
 *
 * Three secondary actions, each a plain word beside the primary Check In so
 * the row still has ONE obvious next action (Principle 2):
 *   - Edit      — move the date, change the service or times.
 *   - No-show   — the dog never came; closes the row, no charge.
 *   - Cancel    — the X: the reservation will not happen; no charge.
 *
 * Presentational only: the surface wires each to `useReservationActions`.
 * Targets are >= --ferro-tap (Principle 7: built for hands).
 */
import { Icon } from '../icons.js';
import './ExpectedRowActions.css';

export interface ExpectedRowActionsProps {
  readonly bookingId: string;
  /** "Biscuit" — names the dog in each button's accessible name. */
  readonly dogLabel: string;
  /** data-testid prefix per surface: `front-desk`, `arrival`, `mobile-arrival`. */
  readonly testIdPrefix: string;
  readonly disabled?: boolean;
  readonly onEdit: () => void;
  readonly onNoShow: () => void;
  readonly onCancel: () => void;
}

export function ExpectedRowActions({
  bookingId,
  dogLabel,
  testIdPrefix,
  disabled = false,
  onEdit,
  onNoShow,
  onCancel,
}: ExpectedRowActionsProps): JSX.Element {
  return (
    <div
      className="ferro-expected-actions"
      role="group"
      aria-label={`Reservation actions for ${dogLabel}`}
    >
      <button
        type="button"
        className="ferro-btn ferro-btn--ghost ferro-expected-actions__btn"
        onClick={onEdit}
        disabled={disabled}
        data-testid={`${testIdPrefix}-edit-${bookingId}`}
        aria-label={`Edit the reservation for ${dogLabel}`}
      >
        Edit
      </button>
      <button
        type="button"
        className="ferro-btn ferro-btn--ghost ferro-expected-actions__btn"
        onClick={onNoShow}
        disabled={disabled}
        data-testid={`${testIdPrefix}-no-show-${bookingId}`}
        aria-label={`Mark ${dogLabel} a no-show`}
      >
        No-show
      </button>
      <button
        type="button"
        className="ferro-btn ferro-btn--ghost ferro-expected-actions__btn ferro-expected-actions__btn--cancel"
        onClick={onCancel}
        disabled={disabled}
        data-testid={`${testIdPrefix}-cancel-${bookingId}`}
        aria-label={`Cancel the reservation for ${dogLabel}`}
      >
        <Icon name="close" size={16} />
        <span>Cancel</span>
      </button>
    </div>
  );
}
